import { useEffect, useRef, useState } from "react";
import { Check, Close, Delete, Edit } from "@mui/icons-material";
import { IconButton, styled, Tooltip } from "@mui/material";
import clsx from "clsx";
import { api } from "api";
import { SmallText, TextFieldCustom } from "components";
import ImageViewer from "components/image-viewer/ImageViewer";
import { useToggle } from "hooks/useToggle";

const ContainerStyled = styled("div")(({ theme }) => ({
  borderRadius: "0.25rem",
  boxSizing: "content-box",
  boxShadow: theme.shadows[1],
  cursor: "pointer",
  "& .file-container": {
    position: "relative",
    borderRadius: "0.25rem 0.25rem 0 0",
    overflow: "hidden",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    "& .file-actions": {
      position: "absolute",
      display: "none",
      top: "0.5rem",
      right: "0.5rem",
      zIndex: 2,
      cursor: "default",
      "& *": {
        cursor: "default",
      },
      "& button": {
        backgroundColor: theme.palette.background.paper,
        opacity: 0.8,
        "&:hover": {
          opacity: 1,
        },
      },
    },
  },
  "& .metadata-container": {
    padding: "0.25rem 0.5rem",
    display: "flex",
    alignItems: "center",
    " p": {
      fontWeight: 600,
      lineHeight: "1rem",
    },
  },
  "&:hover": {
    "& .file-container": {
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        zIndex: 1,
      },
      "& .file-actions": {
        display: "flex",
        gap: "0.25rem",
      },
    },
  },
  "&.disabled": {
    "& .file-container": {
      pointerEvents: "none",
    },
    "&:hover": {
      "& .file-container": {
        "&::after": {
          content: "none",
        },
        "& .file-actions": {
          display: "none",
        },
      },
    },
  },
  "&.editing": {
    "& .metadata-container": {
      padding: "0.25rem",
      position: "relative",
    },
    "& .title-input": {
      "& .MuiInputBase-root": {
        borderRadius: "0.25rem",
        padding: 0,
        "& textarea": {
          padding: "0.25rem",
        },
      },
    },
    "& .input-actions": {
      position: "absolute",
      bottom: "-2.25rem",
      right: "0.25rem",
      display: "flex",
      gap: "0.5rem",
      "& button": {
        borderRadius: "0.25rem",
        boxShadow: theme.shadows[2],
        opacity: 0.8,
        "&:hover": {
          opacity: 1,
        },
      },
    },
  },
}));

const MediaFile = ({ file, onSaveChanges, onDelete, className }) => {
  const inputRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState();

  const { isOpen: isImageOpen, toggle: toggleImage } = useToggle();
  const { isOpen: isEditing, open: startEdit, close: stopEdit } = useToggle();

  const id = file.id;
  const url = file.url;
  const title = file.title;

  const handleEdit = (e) => {
    e.stopPropagation();
    startEdit();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (typeof onDelete === "function") {
      onDelete(file);
    }
  };

  const handleCancelChanges = () => {
    stopEdit();
  };

  const handleSaveChanges = () => {
    const newTitle = inputRef.current.value;

    if (typeof onSaveChanges === "function") {
      onSaveChanges(id, newTitle);
    }

    stopEdit();
  };

  useEffect(() => {
    let fetchedBlobUrl;
    api.actions.media
      .download(url)
      .then((url) => {
        fetchedBlobUrl = url;
        setBlobUrl(fetchedBlobUrl);
      })
      .catch((err) => {
        console.error("Error fetching image:", err);
        setBlobUrl(null);
      });

    return () => {
      if (fetchedBlobUrl) {
        URL.revokeObjectURL(fetchedBlobUrl);
      }
    };
  }, [url]);

  return (
    <>
      <ContainerStyled
        className={clsx({ disabled: isEditing, editing: isEditing }, className)}
      >
        <Tooltip title={title} disableHoverListener={isEditing}>
          <>
            <div className="file-container" onClick={toggleImage}>
              <img src={blobUrl} alt={title} />
              <div className="file-actions">
                <IconButton size="small" onClick={handleEdit}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleDelete}>
                  <Delete fontSize="small" />
                </IconButton>
              </div>
            </div>
            <div className="metadata-container">
              {isEditing ? (
                <>
                  <TextFieldCustom
                    inputRef={inputRef}
                    isMultiline
                    minRows={1}
                    defaultValue={title}
                    className="title-input"
                  />
                  <div className="input-actions">
                    <IconButton size="small" onClick={handleSaveChanges}>
                      <Check fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelChanges}>
                      <Close fontSize="small" />
                    </IconButton>
                  </div>
                </>
              ) : (
                <SmallText className="text-truncate">{title}</SmallText>
              )}
            </div>
          </>
        </Tooltip>
      </ContainerStyled>
      <ImageViewer
        isOpen={isImageOpen}
        onClose={toggleImage}
        src={blobUrl}
        title={title}
      />
    </>
  );
};

export default MediaFile;
